import React from 'react';
import { toExceptionDetail } from 'src/models/ExceptionDetail';
import { redirectToFailurePage } from 'src/utils/ErrorRedirectUtils';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  readonly UNHANDLED_REJECTION = 'unhandledrejection';

  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  promiseRejectionHandler = (event: PromiseRejectionEvent) => {
    event.preventDefault();
    console.error('Exception with reason ', event.reason, undefined, false);
    this.setState({ hasError: true });
    redirectToFailurePage(
      toExceptionDetail(event.reason),
      { source: 'Unhandled promise rejection' },
      true
    );
  };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      'Exception with stack trace ' + info.componentStack,
      error,
      undefined,
      false
    );
    this.setState({ hasError: true });
    redirectToFailurePage(
      toExceptionDetail(error),
      {
        source: 'Uncaught render error',
        componentStack: info.componentStack
      },
      true
    );
  }

  componentDidMount() {
    window.addEventListener(
      this.UNHANDLED_REJECTION,
      this.promiseRejectionHandler
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      this.UNHANDLED_REJECTION,
      this.promiseRejectionHandler
    );
  }

  render() {
    return <>{this.props.children}</>;
  }
}

export default AppErrorBoundary;
